import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

const AssistiveDevicesSection = ({ devicesData }) => (
  <Box sx={{ my: 4 }}>
    {/* Common Devices */}
    {devicesData.commonDevices && (
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          {devicesData.commonDevices.title || "Common Assistive Devices"}
        </Typography>
        <Grid container spacing={3}>
          {devicesData.commonDevices.devices.map((device, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ 
                height: '100%',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 3
                }
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                    {device.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {device.description}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                    Best For:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {device.bestFor}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                    Considerations:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {device.considerations}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                    Proper Use:
                  </Typography>
                  <List dense>
                    {device.properUse.map((item, i) => (
                      <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    )}

    {/* Selection Process */}
    {devicesData.selectionProcess && (
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          {devicesData.selectionProcess.title || "Selection and Fitting Process"}
        </Typography>
        <Card elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <List>
              {devicesData.selectionProcess.steps.map((item, index) => (
                <ListItem key={index} alignItems="flex-start" sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    <Box 
                      sx={{ 
                        width: 28, 
                        height: 28, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.step} 
                    secondary={item.details}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    )}

    {/* Common Errors */}
    {devicesData.commonErrors && (
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          {devicesData.commonErrors.title || "Common Errors and Corrections"}
        </Typography>
        <Grid container spacing={3}>
          {devicesData.commonErrors.errors.map((error, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ 
                height: '100%',
                borderLeft: '4px solid',
                borderColor: 'error.main',
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 3
                }
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600, mb: 2 }}>
                    {error.error}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                    Consequences:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {error.consequences}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                    Correction:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {error.correction}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    )}

    {/* Training Protocol */}
    {devicesData.trainingProtocol && (
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          {devicesData.trainingProtocol.title || "Training Protocol"}
        </Typography>
        <Card elevation={1} sx={{ borderRadius: 2 }}>
          <CardContent>
            <List>
              {devicesData.trainingProtocol.components.map((item, index) => (
                <ListItem key={index} alignItems="flex-start" sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    <Box 
                      sx={{ 
                        width: 28, 
                        height: 28, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.component} 
                    secondary={item.details}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    )}
  </Box>
);

export default AssistiveDevicesSection;
